import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, Shuffle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ProductMixPage = () => {
  const [items, setItems] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ product_id: '', product_name: '', restricted_product_id: '', restricted_product_name: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [mixRes, prodRes] = await Promise.all([api.get('/product-mix'), api.get('/loan-products')]);
      setItems(mixRes.data);
      setLoanProducts(prodRes.data);
    } catch { setItems([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.product_name || !form.restricted_product_name) { toast.error('Both products are required'); return; }
    try { await api.post('/product-mix', form); toast.success('Product mix rule created'); setShowModal(false); setForm({ product_id: '', product_name: '', restricted_product_id: '', restricted_product_name: '' }); load(); }
    catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product mix rule?')) return;
    try { await api.delete(`/product-mix/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const selectProduct = (field, id) => {
    const p = loanProducts.find(lp => lp.id === id);
    if (field === 'product') setForm({ ...form, product_id: id, product_name: p?.name || '' });
    else setForm({ ...form, restricted_product_id: id, restricted_product_name: p?.name || '' });
  };

  return (
    <AdminSubPage title="Product Mix" breadcrumb={['Admin', 'Products', 'Product Mix']} backPath="/dashboard/admin/products" actionLabel="+ Add Rule" onAction={() => setShowModal(true)}>
      <div className="p-4 bg-blue-50 border border-blue-200 text-sm text-blue-800 mb-2">
        Product Mix rules restrict which loan products a client can hold simultaneously. If a client already has Product A, they cannot apply for a restricted Product B.
      </div>
      <div className="bg-white border border-gray-200" data-testid="product-mix-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500"></th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Restricted Product</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={4} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={4} className="text-center py-8 text-gray-400"><Shuffle className="w-8 h-8 mx-auto mb-2 text-gray-300" />No product mix rules defined.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`mix-row-${item.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{item.product_name}</td>
                <td className="px-4 py-3 text-center text-gray-400 text-xs">cannot co-exist with</td>
                <td className="px-4 py-3 font-medium text-sm text-red-700">{item.restricted_product_name}</td>
                <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent aria-describedby="pm-desc"><DialogHeader><DialogTitle>Add Product Mix Rule</DialogTitle></DialogHeader>
          <p id="pm-desc" className="sr-only">Product mix restriction form</p>
          <div className="space-y-4">
            <div><Label>Product *</Label>
              <Select value={form.product_id} onValueChange={v => selectProduct('product', v)}>
                <SelectTrigger data-testid="pm-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{loanProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Restricted Product *</Label>
              <Select value={form.restricted_product_id} onValueChange={v => selectProduct('restricted', v)}>
                <SelectTrigger data-testid="pm-restricted-select"><SelectValue placeholder="Select restricted product" /></SelectTrigger>
                <SelectContent>{loanProducts.filter(p => p.id !== form.product_id).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="pm-submit-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default ProductMixPage;
